class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      autorotate: this.dataset.autorotate === 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
      moveTime: parseFloat(this.dataset.speed), // 100px당 이동 시간 / move time per 100px
      space: 100, // 100px
    };

    // theme 전역 객체 안전 체크 / Safe check for global theme object
    const isSmall = (typeof theme !== 'undefined' && theme.config)
      ? theme.config.mqlSmall
      : window.matchMedia('(max-width: 749px)').matches;

    if (isSmall) {
      this.initOnMobile();
    } else {
      this.initOnDesktop();
    }

    document.addEventListener('matchSmall', () => {
      this.unload();
      this.initOnMobile();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.initOnDesktop();
    });
  }

  initOnDesktop() {
    switch (this.dataset.layout) {
      case 'carousel':
        this.initCarousel();
        break;
      case 'marquee':
        this.initMarquee();
        break;
      default:
        this.initMarqueeOptimized();
    }
  }

  initOnMobile() {
    switch (this.dataset.mobileLayout) {
      case 'carousel':
        this.initCarousel();
        break;
      case 'marquee':
        this.initMarquee();
        break;
    }
  }

  unload() {
    // 바닐라 캐러셀 정리 / Vanilla carousel cleanup
    if (this.vanillaCarousel) {
      this.destroyVanillaCarousel();
      this.removeNextPrevListeners();
    }

    // 마키 정리 / Marquee cleanup
    if (this.classList.contains('marquee')) {
      this.classList.remove('marquee');
      this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((slider) => {
        slider.remove();
      });
    }

    if (this.observer && typeof this.observer.unobserve === 'function') {
      this.observer.unobserve(this);
    }
  }

  initCarousel() {
    // 블록 1개면 캐러셀 불필요 / No carousel needed for single block
    if (this.dataset.blockCount == 1) {
      return;
    }

    const slider = this.querySelector('.announcement-slider');
    this.initVanillaCarousel(slider);

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.onNextPrevClick = this.handleNextPrevClick.bind(this);
    if (this.prevButton) this.prevButton.addEventListener('click', this.onNextPrevClick);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNextPrevClick);
  }

  // ── 바닐라 JS 캐러셀 폴백 / Vanilla JS carousel fallback ──

  initVanillaCarousel(slider) {
    this.vanillaCarousel = true;
    this.currentSlide = 0;
    this.slides = slider.querySelectorAll('.announcement-slider__slide');
    this.totalSlides = this.slides.length;

    if (this.totalSlides < 2) return;

    // 인라인 스타일로 직접 제어 (CSS 우선순위 충돌 방지)
    // Control via inline styles to bypass CSS specificity conflicts
    this.showSlide(0);

    // 자동 회전 / Auto-rotate
    if (this.config.autorotate) {
      this.startAutoplay();
    }
  }

  showSlide(index) {
    if (!this.slides) return;

    // 인라인 스타일이라 테마 CSS를 이김 (스타일시트 !important 는 없음 — 2026-09-23 실측)
    // Inline style already beats theme CSS (no stylesheet !important targets slides — measured 2026-09-23)
    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.style.cssText = 'display: flex; width: 100%; height: 100%; align-items: center; justify-content: center;';
      } else {
        slide.style.cssText = 'display: none;';
      }
    });
    this.currentSlide = index;
  }

  nextSlide() {
    const next = (this.currentSlide + 1) % this.totalSlides;
    this.showSlide(next);
  }

  prevSlide() {
    const prev = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.showSlide(prev);
  }

  startAutoplay() {
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.config.autorotateSpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  destroyVanillaCarousel() {
    this.stopAutoplay();

    // 인라인 스타일 제거 / Remove inline styles
    if (this.slides) {
      this.slides.forEach((slide) => {
        slide.style.cssText = '';
      });
    }

    this.vanillaCarousel = false;
    this.slides = null;
    this.currentSlide = 0;
  }

  // ── 네비게이션 / Navigation ──

  removeNextPrevListeners() {
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onNextPrevClick);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNextPrevClick);
  }

  handleNextPrevClick(event) {
    event.preventDefault();
    const { name } = event.currentTarget;

    if (!this.vanillaCarousel) return;

    this.stopAutoplay();
    if (name === 'next') this.nextSlide();
    else if (name === 'previous') this.prevSlide();
    if (this.config.autorotate) this.startAutoplay();
  }

  // ── 마키 / Marquee ──

  initMarqueeOptimized() {
    setTimeout(() => {
      this.marqueeAnimation();
    });

    // 화면 밖일 때 일시정지 / Pause when out of view
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.isAnimated && this.classList.remove('marquee--paused');
        } else {
          this.isAnimated && this.classList.add('marquee--paused');
        }
      });
    }, { rootMargin: '0px 0px 50px 0px' });

    this.observer.observe(this);
    window.addEventListener('resize', this.marqueeAnimation.bind(this));
  }

  marqueeAnimation() {
    const slider = this.querySelector('.announcement-slider');

    if (slider.scrollWidth > this.clientWidth) {
      if (!this.isAnimated) {
        for (let index = 0; index < 2; index++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }

        this.classList.add('marquee');
        this.style.setProperty('--duration', `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`);
        this.isAnimated = true;
      }
    } else if (this.isAnimated) {
      for (let index = 0; index < 2; index++) {
        this.removeChild(this.lastElementChild);
      }
      this.classList.remove('marquee');
      this.isAnimated = false;
    }
  }

  initMarquee() {
    setTimeout(() => {
      const slider = this.querySelector('.announcement-slider');

      for (let index = 0; index < 10; index++) {
        const clone = slider.cloneNode(true);
        clone.setAttribute('aria-hidden', true);
        this.appendChild(clone);
      }

      this.classList.add('marquee');
      this.style.setProperty('--duration', `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`);
    });

    // 화면 밖일 때 일시정지 / Pause when out of view
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.classList.remove('marquee--paused');
        } else {
          this.classList.add('marquee--paused');
        }
      });
    }, { rootMargin: '0px 0px 50px 0px' });

    this.observer.observe(this);
  }
}

customElements.define('announcement-bar', AnnouncementBar);